import { FC } from 'react';

import { StorageLocation } from 'src/apis/monolith/types';
import { TableActions } from 'src/components/buttons-and-actions/table-actions';
import { Table } from 'src/components/data-display/smart-table';
import {
  GenericSortableHeader,
  Td,
  Th,
} from 'src/components/data-display/table';
import { Stack } from 'src/components/layout/stack';
import { ExternalLink } from 'src/components/navigation/link';
import { Heading } from 'src/components/text/heading';
import { Successful } from 'src/hooks/use-axios';
import { useAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useTitle } from 'src/hooks/use-title';
import { FilterCard } from 'src/pages/storage/locations/filter-card';
import { QuickFiltersBar } from 'src/pages/storage/locations/quick-filters-bar';
import { PageParams } from 'src/pages/storage/locations/use-page-params';
import { StorageLocationsRequest } from 'src/pages/storage/locations/use-storage-locations';

export const Page: FC<{
  storageLocations: Successful<StorageLocationsRequest>;
  pageParams: PageParams;
  filteredStorageLocations: { totalSize: number; items: StorageLocation[] };
}> = ({ storageLocations, pageParams, filteredStorageLocations }) => {
  useTitle('Storage Locations');
  const monolithUser = useAuthenticatedMonolithUser();

  return (
    <Stack flow="row" gap={2}>
      <Stack flow="column" justifyContent="space-between" gap={2}>
        <Heading mode="title" data-testid="header-storage-locations">
          Storage Locations
        </Heading>
        <ExternalLink
          mode="button-primary"
          href={`${PRISMA_CONFIG.oldFrontendUrl}/#/storage/locations/create`}
          data-testid="add-storage-location-button"
        >
          Add Storage Location
        </ExternalLink>
      </Stack>

      <FilterCard pageParams={pageParams} />

      <QuickFiltersBar pageParams={pageParams} />

      <Table
        data-testid="networkPoints"
        pending={storageLocations.pending}
        paginated={{
          perPage: pageParams.value.pageSize,
          start: pageParams.value.start,
          totalSize: filteredStorageLocations.totalSize,
        }}
        data={filteredStorageLocations.items}
        setId={(item) => item.refId}
        cols={[
          {
            key: 'name',
            head: (
              <GenericSortableHeader
                label={pageParams.filterLabels.name}
                sortableParams={[pageParams.value, pageParams.set]}
                column="name"
              />
            ),
            body: (data) => (
              <Td data-testid={`location-name-${data.name}`}>{data.name}</Td>
            ),
          },
          {
            key: 'ssoShortName',
            head: (
              <GenericSortableHeader
                label={pageParams.filterLabels.ssoShortName}
                sortableParams={[pageParams.value, pageParams.set]}
                column="ssoShortName"
              />
            ),
            body: (data) => (
              <Td data-testid={`ssoShortName-${data.ssoName}`}>
                {data.ssoName}
              </Td>
            ),
          },
          {
            key: 'marketArea',
            head: <Th>Market Area</Th>,
            body: (data) => (
              <Td data-testid={`market-area-${data.ssoName}`}>
                {data.marketAreaList.join(', ')}
              </Td>
            ),
          },
          {
            key: 'refId',
            head: (
              <GenericSortableHeader
                label={pageParams.filterLabels.refId}
                sortableParams={[pageParams.value, pageParams.set]}
                column="refId"
              />
            ),
            body: (data) => (
              <Td data-testid={`location-id-${data.refId}`}>{data.refId}</Td>
            ),
          },
          {
            key: 'actions',
            width: '10rem',
            head: <Th align="center">Actions</Th>,
            body: (data) => (
              <Td data-testid={`actions-${data.refId}`} align="center">
                <TableActions>
                  <ExternalLink
                    href={`${PRISMA_CONFIG.oldFrontendUrl}/#/storage/locations/details/${data.refId}`}
                  >
                    Details
                  </ExternalLink>
                  {monolithUser.tsoName === data.ssoName && (
                    <ExternalLink
                      href={`${PRISMA_CONFIG.oldFrontendUrl}/#/storage/locations/edit/${data.refId}`}
                    >
                      Edit Storage Location
                    </ExternalLink>
                  )}
                </TableActions>
              </Td>
            ),
          },
        ]}
        empty={{
          label: 'No results match your search criteria.',
        }}
      />
    </Stack>
  );
};
